import { dedent } from 'ts-dedent';
import { ColumnDataType, Provider, WorkflowCodeType, WorkflowComponentParamType, WorkflowComponentParameterMode, WorkflowParameterValueErrorType } from '../types';
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent';
class FindReplace extends SingleOutputWorkflowComponent {
    constructor() {
        super({
            name: 'native.findreplace',
            group: 'Data Preparation',
            title: 'Find and Replace',
            description: `
        This component finds a string in one column of a table and replace it with the specified value from another table.
        `,
            starred: false,
            deprecated: false,
            tags: ['Alterix', 'Compatibility']
        }, [
            {
                name: 'source',
                type: WorkflowComponentParamType.InputTable,
                title: 'Source table'
            },
            {
                name: 'column',
                type: WorkflowComponentParamType.Column,
                title: 'Find within column',
                parent: 'source',
                mode: WorkflowComponentParameterMode.Multiple,
                dataType: [ColumnDataType.String]
            },
            {
                name: 'lookup',
                type: WorkflowComponentParamType.InputTable,
                title: 'Look-up table'
            },
            {
                name: 'tochange',
                type: WorkflowComponentParamType.Column,
                title: 'Find value column',
                parent: 'lookup',
                dataType: [ColumnDataType.String]
            },
            {
                name: 'replacement',
                type: WorkflowComponentParamType.Column,
                title: 'Replacement column',
                parent: 'lookup',
                dataType: [ColumnDataType.String]
            },
            {
                name: 'findmode',
                type: WorkflowComponentParamType.Selection,
                title: 'Find mode',
                options: [
                    'Beginning of column',
                    'Any part of column',
                    'End of column'
                ],
                default: 'Any part of column'
            },
            {
                name: 'caseinsensitive',
                type: WorkflowComponentParamType.Boolean,
                title: 'Case insensitive find',
                default: false
            },
            {
                name: 'matchwholeword',
                type: WorkflowComponentParamType.Boolean,
                title: 'Match whole word only',
                default: false
            },
            {
                name: 'mode',
                type: WorkflowComponentParamType.Selection,
                title: 'Mode',
                options: [
                    'Replace found text with value',
                    'Append column(s) to record'
                ],
                default: 'Replace found text with value'
            },
            {
                name: 'toappend',
                type: WorkflowComponentParamType.Column,
                title: 'Columns(s) to append',
                parent: 'lookup',
                dataType: [ColumnDataType.String],
                mode: WorkflowComponentParameterMode.Multiple,
                optional: true,
                showIf: [
                    {
                        parameter: 'mode',
                        value: 'Append column(s) to record'
                    }
                ]
            }
        ]);
        this.supportedProviders = [
            Provider.BigQuery,
            Provider.Snowflake,
            Provider.Postgres
        ];
    }
    // Inspired by:
    // https://dba.stackexchange.com/questions/319170/how-to-use-replace-text-function-in-a-column-using-another-table-as-a-lookup-ta
    async getCode({ workflow, params, connection, codeType, schemas }) {
        const dryRun = codeType === WorkflowCodeType.DryRun;
        let outputTablename = connection.getOutputTableName(workflow.id, this.metadata.name, params, 'result', codeType);
        const preparedParams = this._prepareParameters(params);
        const sourceT = connection.quoteName(params.source);
        const lookupT = connection.quoteName(params.lookup);
        let regexpHead = '';
        let regexpTail = '';
        let regexpReplaceOptions = '';
        let regexpMatchOptions = '';
        const boundary = connection.type === Provider.Postgres ? '\\y' : '\\\\b';
        switch (preparedParams.findmode) {
            case 'Beginning of column':
                regexpHead = '^';
                regexpTail = preparedParams.matchwholeword ? boundary : '';
                break;
            case 'End of column':
                regexpTail = '$';
                regexpHead = preparedParams.matchwholeword ? boundary : '';
                break;
            case 'Any part of column':
                regexpHead = regexpTail = preparedParams.matchwholeword ? boundary : '';
                break;
        }
        if (preparedParams.caseinsensitive) {
            if (connection.type === Provider.Snowflake) {
                regexpReplaceOptions = ", 1, 0, 'i'";
                regexpMatchOptions = ", 1, 1, 0, 'i'";
            }
            else {
                regexpHead = '(?i)' + regexpHead;
            }
        }
        let matchFunc = '';
        if (connection.type === Provider.BigQuery) {
            matchFunc = dedent `
                    REGEXP_CONTAINS(
                      rt.replaced,
                      '${regexpHead}' ||
                      l.${preparedParams.tochange} ||
                      '${regexpTail}'
                    )`;
        }
        else if (connection.type === Provider.Snowflake) {
            matchFunc = dedent `
                    REGEXP_INSTR(
                      rt.replaced,
                      '${regexpHead}' ||
                      l.${preparedParams.tochange} ||
                      '${regexpTail}'${regexpMatchOptions}
                    ) != 0`;
        }
        else if (connection.type === Provider.Postgres) {
            matchFunc = dedent `
                      rt.replaced ~ (
                        '${regexpHead}' ||
                        l.${preparedParams.tochange} ||
                        '${regexpTail}'
                      )`;
        }
        let select = '';
        if (dryRun) {
            if (preparedParams.mode === 'Append column(s) to record') {
                select = dedent `
        SELECT
          st.*,
          st.${preparedParams.column} as value,
          st.${preparedParams.column} as replaced,
          ${preparedParams.toappend
                    .map((col) => {
                    return `l.${col} AS ${col}`;
                })
                    .join(',\n')}
        FROM
          ${sourceT} st,
          ${lookupT} l
        `;
            }
            else {
                select = dedent `
        SELECT
          *,
          ${preparedParams.column} as __replaced
        FROM
          ${sourceT}
        `;
            }
        }
        else {
            if (preparedParams.mode === 'Append column(s) to record') {
                const cols = preparedParams.toappend
                    .map((col) => {
                    return `l.${col} AS ${col}`;
                })
                    .join(',\n');
                select = dedent `
        WITH RECURSIVE rt AS (
          SELECT
            st.${preparedParams.column} as value,
            st.${preparedParams.column} as replaced,
            ${dedent(cols)}
          FROM
            ${sourceT} st,
            ${lookupT} l
          UNION ALL
            SELECT
              rt.value,
              REGEXP_REPLACE(
                rt.replaced,
                '${regexpHead}' ||
                l.${preparedParams.tochange} ||
                '${regexpTail}',
                l.${preparedParams.replacement}${regexpReplaceOptions}
              ),
              ${dedent(cols)}
            FROM rt
            JOIN ${lookupT} l
            ON ${matchFunc}

        ),
        replacements AS
        (
          SELECT rt.* FROM rt
            LEFT JOIN  ${lookupT} l
            ON ${matchFunc}
            WHERE l.${preparedParams.tochange} IS NULL
        )
        SELECT DISTINCT
          st.*,
          rp.*
        FROM ${sourceT} st, replacements rp
        WHERE st.${preparedParams.column} = rp.value`;
            }
            else {
                select = dedent `
                WITH RECURSIVE rt AS (
                  SELECT
                    st.${preparedParams.column} as value,
                    st.${preparedParams.column} as replaced
                  FROM
                    ${sourceT} st
                  UNION ALL
                    SELECT
                      rt.value AS value,
                      REGEXP_REPLACE(
                        rt.replaced,
                        '${regexpHead}' ||
                        l.${preparedParams.tochange} ||
                        '${regexpTail}',
                        l.${preparedParams.replacement}${regexpReplaceOptions}
                      ) AS replaced
                    FROM rt
                    JOIN  ${lookupT} l
                    ON ${matchFunc}
                ),
                replacements AS
                (
                  SELECT rt.* FROM rt
                    LEFT JOIN  ${lookupT} l
                    ON ${matchFunc}
                    WHERE l.${preparedParams.tochange} IS NULL
                )
                SELECT DISTINCT
                  st.*,
                  rp.replaced AS __replaced
                FROM ${sourceT} st, replacements rp
                WHERE st.${preparedParams.column} = rp.value`;
            }
        }
        let query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null);
        const ifExist = connection.type !== Provider.Postgres ? 'IF EXISTS' : '';
        if (preparedParams.mode === 'Replace found text with value') {
            query = dedent `${query}
              ALTER TABLE ${connection.quoteName(outputTablename)}
              DROP COLUMN ${ifExist} ${preparedParams.column};
              ALTER TABLE ${connection.quoteName(outputTablename)}
              RENAME COLUMN ${ifExist} __replaced TO ${preparedParams.column};`;
        }
        else {
            query = dedent `${query}
                ALTER TABLE ${connection.quoteName(outputTablename)}
                DROP COLUMN ${ifExist} replaced;
                ALTER TABLE ${connection.quoteName(outputTablename)}
                DROP COLUMN ${ifExist} value;`;
        }
        return {
            query,
            outputs: { result: outputTablename }
        };
    }
    checkInputValues({ params, resolvedParamValues, connection, dryRun, schemas, variables }) {
        const problems = super.checkInputValues({
            params,
            resolvedParamValues,
            connection,
            dryRun,
            schemas,
            variables
        });
        const preparedParams = this._prepareParameters(resolvedParamValues);
        if (schemas && params.source) {
            const table = params.source;
            if (table.value) {
                const { component, output } = table.value;
                const componentSchemas = schemas.filter((x) => x.componentName === component)?.[0];
                if (componentSchemas) {
                    const tableSchema = componentSchemas.schemas.filter((x) => x.name === output)?.[0];
                    if (tableSchema && tableSchema.schema) {
                        tableSchema.schema.forEach((col) => {
                            if ([ColumnDataType.Geography, ColumnDataType.Geometry].includes(col.type)) {
                                problems.push({
                                    parameter: 'column',
                                    type: WorkflowParameterValueErrorType.WrongValue,
                                    message: `Column ${col.name} is of type ${col.type}. The input table cannot contain geo columns.`
                                });
                            }
                        });
                    }
                }
            }
        }
        return problems;
    }
}
export { FindReplace };
